import { SEO } from "@/components/SEO";
import { PageSchema } from "@/components/seo/PageSchema";
import { LastUpdated } from "@/components/seo/LastUpdated";
import { TopCTA } from "@/components/cta/TopCTA";
import { MidPageCTA } from "@/components/cta/MidPageCTA";
import { BottomCTA } from "@/components/cta/BottomCTA";
import { SymptomChecker } from "@/components/content/SymptomChecker";
import { FixSteps } from "@/components/content/FixSteps";
import { DIYvsDoneForYou } from "@/components/content/DIYvsDoneForYou";
import { FAQSection } from "@/components/content/FAQSection";
import { InternalLinks } from "@/components/content/InternalLinks";
import { LeadFormSection } from "@/components/LeadFormSection";

const LandscapingWebsiteSlow = () => {
  const symptoms = [
    "Portfolio galleries take forever to load",
    "Google PageSpeed score is under 40",
    "High bounce rate despite quality traffic",
    "Visitors complain about slow experience",
    "Site freezes or crashes on mobile devices",
    "Images load progressively or appear blurry first"
  ];

  const rootCauses = [
    { cause: "High-resolution portfolio images not optimized", impact: "Each 5MB image adds 5-10 seconds on mobile" },
    { cause: "Gallery plugins loading all images at once", impact: "Dozens of images loading simultaneously overwhelms browsers" },
    { cause: "Embedded videos auto-playing", impact: "Video files are massive and drain mobile data" },
    { cause: "Shared hosting can't handle image-heavy pages", impact: "Server struggles with multiple image requests" },
    { cause: "No CDN for image delivery", impact: "Images travel long distances, adding latency" },
    { cause: "Unoptimized WordPress theme", impact: "Bloated themes with unnecessary features slow everything" },
    { cause: "Too many third-party scripts", impact: "Analytics, chat widgets, and plugins add up" },
    { cause: "Missing browser caching", impact: "Every visit reloads everything from scratch" }
  ];

  const fixSteps = [
    { step: "Compress portfolio images to under 300KB each", details: "Use WebP format and tools like ShortPixel for best quality-to-size ratio" },
    { step: "Implement lazy loading for galleries", details: "Only load images as visitors scroll down, not all at once" },
    { step: "Replace auto-play videos with thumbnails", details: "Let visitors click to play, saving massive bandwidth" },
    { step: "Upgrade to managed WordPress hosting", details: "Cloudways, WP Engine, or Kinsta handle image-heavy sites better" },
    { step: "Set up Cloudflare CDN", details: "Free CDN delivers images from servers closest to each visitor" },
    { step: "Enable browser caching", details: "Returning visitors load cached images instantly" },
    { step: "Audit and remove unnecessary plugins", details: "Each plugin adds load time—keep only essentials" }
  ];

  const faqs = [
    { question: "Why are landscaping websites especially prone to speed issues?", answer: "Landscaping websites typically feature large portfolios with high-resolution before/after photos. Without proper optimization, these images create severe speed problems. A single unoptimized 5MB image can add 10+ seconds to load time on mobile." },
    { question: "How fast should my landscaping website load?", answer: "Under 3 seconds on mobile and under 2 seconds on desktop. With image-heavy portfolios, this requires intentional optimization—it won't happen automatically. Most unoptimized landscaping sites load in 8-15 seconds." },
    { question: "Will compressing images hurt my portfolio quality?", answer: "Not if done correctly. Modern compression techniques (WebP format, intelligent compression) can reduce file sizes by 80% while maintaining visual quality. Your portfolio will look just as impressive but load 5x faster." },
    { question: "Should I remove portfolio images to speed up my site?", answer: "No—your portfolio is essential for landscaping conversions. Instead, optimize existing images, use lazy loading, and consider showing thumbnails that expand on click rather than full-size images in galleries." },
    { question: "How do I check my landscaping website's speed?", answer: "Use Google PageSpeed Insights and enter your URL. Test both mobile and desktop. For landscaping sites, also test individual portfolio pages, as they're often the slowest. Aim for 70+ mobile score." },
    { question: "Is my hosting provider causing speed issues?", answer: "Very possibly. Landscaping sites with large image galleries need quality hosting. Shared hosting ($5-15/month) often can't handle the load. Managed hosting ($25-50/month) provides better performance for image-heavy sites." },
    { question: "What's lazy loading and why does it help?", answer: "Lazy loading delays loading images until visitors scroll to them. Instead of loading 50 portfolio images at once, only visible images load initially. This dramatically speeds up initial page load while still displaying your full portfolio." },
    { question: "How much does slow speed cost in lost leads?", answer: "Significantly. Each additional second of load time reduces conversions by 7%. A landscaping website loading in 10 seconds instead of 3 could be losing 49% of potential leads to speed alone—before they even see your work." },
    { question: "Can I speed up my landscaping website myself?", answer: "Basic optimizations like image compression are DIY-friendly. However, comprehensive speed optimization (server configuration, code optimization, CDN setup) typically requires technical expertise. For image-heavy landscaping sites, professional help often delivers better results." },
    { question: "Will a faster website help my Google rankings?", answer: "Yes. Page speed is a confirmed Google ranking factor, especially for mobile searches. Faster landscaping websites rank higher and attract more organic traffic, compounding the lead generation benefit." }
  ];

  const links = [
    { to: "/websites/", label: "Website Subscription Pricing" },
    { to: "/landscaping-website-design/", label: "Landscaping Website Design" },
    { to: "/website-fixes/landscaping-website-not-getting-leads/", label: "Website Not Getting Leads" },
    { to: "/landscaping-website-seo/", label: "Landscaping Website SEO" }
  ];

  return (
    <>
      <SEO
        title="Landscaping Website Speed Optimization Guide | Leaf Rush Marketing"
        description="Is your landscaping website slow? Learn how to optimize image-heavy portfolios for fast loading without sacrificing quality."
        url="https://www.leafrushmarketing.com/website-fixes/landscaping-website-slow/"
      />
      <PageSchema
        url="https://www.leafrushmarketing.com/website-fixes/landscaping-website-slow/"
        title="Landscaping Website Speed Optimization Guide"
        description="Is your landscaping website slow? Learn how to optimize image-heavy portfolios for fast loading."
        faqs={faqs}
      />
      <LastUpdated />

      <article className="py-12">
        <div className="container mx-auto px-4 max-w-4xl">
          <header className="mb-8">
            <h1 className="text-4xl md:text-5xl font-bold text-foreground mb-4">
              Landscaping Website Speed Optimization Guide
            </h1>
            <p className="text-xl text-muted-foreground">
              Your stunning portfolio is killing your load times. Here's how to keep the quality and fix the speed.
            </p>
          </header>

          <TopCTA />

          <SymptomChecker symptoms={symptoms} title="Signs Your Portfolio Is Slowing You Down" />

          <section className="my-12">
            <h2 className="text-3xl font-bold text-foreground mb-6">Root Causes: Why Landscaping Sites Are Slow</h2>
            <div className="grid md:grid-cols-2 gap-4">
              {rootCauses.map((item, index) => (
                <div key={index} className="p-4 bg-muted rounded-lg">
                  <h3 className="font-semibold text-foreground">{item.cause}</h3>
                  <p className="text-sm text-muted-foreground mt-1">{item.impact}</p>
                </div>
              ))}
            </div>
          </section>

          <MidPageCTA />

          <FixSteps steps={fixSteps} title="7 Steps to Speed Up Your Image-Heavy Website" />

          <section className="my-12">
            <h2 className="text-3xl font-bold text-foreground mb-6">The Portfolio Paradox: Quality vs. Speed</h2>
            <div className="prose prose-lg max-w-none text-foreground">
              <p>
                Landscapers face a unique challenge: you need high-quality images to showcase your work, but those same images can destroy your website's speed. The solution isn't removing images—it's optimizing them intelligently.
              </p>
              <p>
                <strong>Modern image optimization can reduce file sizes by 80% without visible quality loss.</strong> Combined with lazy loading and proper hosting, you can have a stunning portfolio that loads in under 3 seconds.
              </p>
              <p>
                At <a href="/websites/" className="text-primary hover:underline">Leaf Rush Marketing</a>, we build landscaping websites optimized for both beauty and speed. Our sites feature impressive portfolios that load fast on any device, ensuring you never lose leads to slow pages.
              </p>
            </div>
          </section>

          <DIYvsDoneForYou />

          <FAQSection faqs={faqs} />

          <InternalLinks links={links} title="Related Resources" />

          <BottomCTA />

          <LeadFormSection title="Get a Free Speed Audit" />
        </div>
      </article>
    </>
  );
};

export default LandscapingWebsiteSlow;
